import React from "react";
import BoltIcon from "@mui/icons-material/Bolt";
import BusinessIcon from "@mui/icons-material/Business";
import DevicesIcon from "@mui/icons-material/Devices";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

// Nav config for the Energy sidebar (EnergyPortalSidebar). Structurally
// mirrors LcpPortalNavigation + environmentalNavigation — same `collapse`
// shape, same gate helpers — so the same sidebar chrome can render either
// tree. v1 ships with the Control Center entry + the cross-system
// Settings group (Organization/Users/Assets/Devices), each mounting the
// AFMS settings page through a thin wrapper.
const primaryRoutes = [
  {
    type: "collapse",
    name: "Energy Control Center",
    key: "energy-nav-control-center",
    route: "/energy/control-center",
    noCollapse: true,
    icon: <BoltIcon fontSize="small" />,
    collapse: [],
  },
];

const settingsGroup = {
  // Energy-rebranded Settings group. Sub-items lift their data from the
  // existing super-admin org pages via thin wrappers under
  // src/energy/pages/Settings/* — same pattern LCP + AFMS use.
  type: "collapse",
  name: "Settings",
  key: "energy-nav-settings",
  icon: <SettingsOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Organization",
      key: "energy-nav-settings-organization",
      route: "/energy/settings/organization",
      icon: <BusinessIcon fontSize="small" />,
    },
    {
      name: "Users",
      key: "energy-nav-settings-users",
      route: "/energy/settings/users",
      icon: <PeopleAltIcon fontSize="small" />,
    },
    {
      name: "Assets",
      key: "energy-nav-settings-assets",
      route: "/energy/settings/assets",
      icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
    },
    {
      name: "Devices",
      key: "energy-nav-settings-devices",
      route: "/energy/settings/devices",
      icon: <DevicesIcon fontSize="small" />,
    },
  ],
};

// Enterprise group — same shape used in the AFMS + LCP portals. Hidden
// unless the user's primary role is OWNER AND their org has
// IsEnterprise=true (see itemPassesGate below).
const enterpriseGroup = {
  type: "collapse",
  name: "Enterprise",
  key: "energy-nav-enterprise",
  icon: <ApartmentOutlinedIcon fontSize="small" />,
  requiresEnterpriseOwner: true,
  collapse: [
    {
      name: "Manage my Clients",
      key: "energy-nav-enterprise-clients",
      route: "/energy/enterprise/manage-clients",
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      requiresEnterpriseOwner: true,
    },
    {
      name: "Manage my Vendors",
      key: "energy-nav-enterprise-vendors",
      route: "/energy/enterprise/manage-vendors",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      requiresEnterpriseOwner: true,
    },
  ],
};

const energyPortalRoutes = [...primaryRoutes, settingsGroup, enterpriseGroup];

// Per-item gate. Energy has no per-action capabilities yet, but the
// Enterprise menu does need to gate on org-level flags so non-enterprise
// orgs / non-OWNER users don't see it.
export const itemPassesGate = (route, _allowedActions, _isPlatformAdmin, orgContext = {}) => {
  if (!route) return false;
  if (route.requiresEnterpriseOwner) {
    const isOwner = (orgContext.role || "").toUpperCase() === "OWNER";
    const isEnterprise = orgContext.isEnterpriseOrganization === true;
    if (!isOwner || !isEnterprise) return false;
  }
  return true;
};

const filterRoutesByCapabilities = (routes, allowedActions, isPlatformAdmin, orgContext) =>
  routes
    .map((route) => {
      if (route.type === "title" || route.type === "divider") return route;
      if (!itemPassesGate(route, allowedActions, isPlatformAdmin, orgContext)) return null;
      if (!route.collapse) return route;
      const collapse = filterRoutesByCapabilities(
        route.collapse,
        allowedActions,
        isPlatformAdmin,
        orgContext
      );
      if (route.type === "collapse" && collapse.length === 0 && !route.route) return null;
      return { ...route, collapse };
    })
    .filter(Boolean);

const stripOrphanTitles = (routes) =>
  routes.filter((route, index) => {
    if (route.type !== "title") return true;
    for (let i = index + 1; i < routes.length; i += 1) {
      if (routes[i].type === "title") return false;
      if (routes[i].type !== "divider") return true;
    }
    return false;
  });

export const getEnergyPortalRoutes = (capabilities, orgContext = {}) =>
  stripOrphanTitles(
    filterRoutesByCapabilities(energyPortalRoutes, null, false, orgContext)
  );

export default energyPortalRoutes;
